import { api } from '../api'
import type { ReplaySourceFormat, UploadRequest } from '../types'

const maxObjectSize = 48 * 2 ** 40
const maxMCAPSegments = 64
const forbiddenBrowserHeaders = new Set(['connection', 'content-length', 'host', 'origin', 'referer'])
const blackBoxLocateSuffixes = [
  '_encoderrtslamfusionloc.txt',
  '_encoderrtslamstmloc.txt',
  '_encoderrtslamloc.txt',
  '_encoderstmloc.txt',
  '_rtslamstmloc.txt',
  '_encoderloc.txt',
  '_rtslamloc.txt',
  '_stmloc.txt',
]

export interface ImportProgress {
  completedBytes: number
  totalBytes: number
  stage: string
}

export function validateReplayFiles(format: ReplaySourceFormat, files: Pick<File, 'name' | 'size'>[]): void {
  if (files.length === 0) throw new Error('请选择至少一个回放文件')
  if (files.length > maxMCAPSegments) throw new Error(`一次最多导入 64 个${format === 'mcap' ? ' MCAP 分段' : ' BlackBox 文件'}`)
  if (format === 'ycr' && files.length !== 1) throw new Error('YCR 每次只能导入一个数据库文件')
  if (format === 'blackbox') {
    let databases = 0
    for (const file of files) {
      const name = file.name.trim().toLowerCase()
      if (/\.(db|sqlite|sqlite3)$/.test(name)) {
        databases += 1
        continue
      }
      const strategy = blackBoxLocateSuffixes.find((suffix) => name.endsWith(suffix))
      if (!strategy) throw new Error(`${file.name} 不是受支持的 BlackBox *Loc.txt 定位策略文件`)
    }
    if (databases !== 1) throw new Error(`BlackBox 必须且只能包含一个 SQLite 数据库，当前为 ${databases} 个`)
  }
  for (const file of files) {
    if (!file.name.trim()) throw new Error('文件名不能为空')
    if (file.size <= 0 || file.size > maxObjectSize) throw new Error(`${file.name} 的大小必须在 1 byte–48 TiB 之间`)
  }
}

export function normalizeETag(value: string | null): string {
  return (value ?? '').trim().replace(/^"|"$/g, '')
}

function putObject(
  request: UploadRequest,
  content: Blob,
  onProgress: (uploadedBytes: number) => void,
  signal?: AbortSignal,
  bearerToken?: string,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const cleanup = () => signal?.removeEventListener('abort', abort)
    const abort = () => xhr.abort()
    xhr.open(request.method || 'PUT', request.url, true)
    if (bearerToken) xhr.setRequestHeader('Authorization', `Bearer ${bearerToken}`)
    for (const [name, value] of Object.entries(request.headers ?? {})) {
      if (!forbiddenBrowserHeaders.has(name.toLowerCase())) xhr.setRequestHeader(name, value)
    }
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded)
    }
    xhr.onerror = () => {
      cleanup()
      reject(new Error(bearerToken ? '平台直传失败；请检查网络、Ingress 和 PVC 状态' : 'TOS 上传失败；请检查 bucket CORS、网络和预签名 URL 是否过期'))
    }
    xhr.onabort = () => {
      cleanup()
      reject(new DOMException('上传已取消', 'AbortError'))
    }
    xhr.onload = () => {
      cleanup()
      if (xhr.status < 200 || xhr.status >= 300) {
        let detail = ''
        try { detail = String(JSON.parse(xhr.responseText)?.error ?? '') } catch { detail = xhr.responseText.trim() }
        reject(new Error(`${bearerToken ? '平台直传' : 'TOS 上传'}失败：${detail || `HTTP ${xhr.status}`}`))
        return
      }
      onProgress(content.size)
      resolve(normalizeETag(xhr.getResponseHeader('ETag')))
    }
    if (signal?.aborted) {
      reject(new DOMException('上传已取消', 'AbortError'))
      return
    }
    signal?.addEventListener('abort', abort, { once: true })
    xhr.send(content)
  })
}

async function uploadFile(
  token: string,
  format: ReplaySourceFormat,
  file: File,
  precedingBytes: number,
  totalBytes: number,
  notify: (progress: ImportProgress) => void,
  signal?: AbortSignal,
): Promise<string> {
  notify({ completedBytes: precedingBytes, totalBytes, stage: `正在为 ${file.name} 创建不可变对象…` })
  const plan = await api.createUpload(token, file, format, signal)
  if (plan.mode === 'direct') {
    await putObject(plan.upload, file, (uploaded) => notify({ completedBytes: precedingBytes + uploaded, totalBytes, stage: `正在写入平台存储 · ${file.name}` }), signal, token)
    return plan.objectKey
  }
  if (plan.mode === 'single') {
    await putObject(plan.upload, file, (uploaded) => notify({ completedBytes: precedingBytes + uploaded, totalBytes, stage: `正在上传 ${file.name}` }), signal)
    return plan.objectKey
  }

  const completed: { partNumber: number; etag: string }[] = []
  try {
    for (let partNumber = 1; partNumber <= plan.partCount; partNumber += 1) {
      const start = (partNumber - 1) * plan.partSize
      const end = Math.min(file.size, start + plan.partSize)
      const signed = await api.signMultipartPart(token, plan.objectKey, plan.uploadId, partNumber, signal)
      const etag = await putObject(
        signed.upload,
        file.slice(start, end),
        (uploaded) => notify({
          completedBytes: precedingBytes + start + uploaded,
          totalBytes,
          stage: `正在上传 ${file.name} · 分片 ${partNumber}/${plan.partCount}`,
        }),
        signal,
      )
      if (!etag) throw new Error('TOS 未暴露 ETag；请在 bucket CORS 中添加 ExposeHeaders: ETag')
      completed.push({ partNumber, etag })
    }
    notify({ completedBytes: precedingBytes + file.size, totalBytes, stage: `正在合并 ${file.name} 的 ${plan.partCount} 个分片…` })
    await api.completeMultipart(token, plan.objectKey, plan.uploadId, completed, signal)
    return plan.objectKey
  } catch (error) {
    await api.abortMultipart(token, plan.objectKey, plan.uploadId).catch(() => undefined)
    throw error
  }
}

export async function uploadReplayFiles(
  token: string,
  format: ReplaySourceFormat,
  files: File[],
  notify: (progress: ImportProgress) => void,
  signal?: AbortSignal,
): Promise<string[]> {
  validateReplayFiles(format, files)
  const totalBytes = files.reduce((sum, file) => sum + file.size, 0)
  const objectKeys: string[] = []
  let precedingBytes = 0
  for (const file of files) {
    objectKeys.push(await uploadFile(token, format, file, precedingBytes, totalBytes, notify, signal))
    precedingBytes += file.size
  }
  notify({ completedBytes: totalBytes, totalBytes, stage: '上传完成，正在提交后台校验与索引…' })
  return objectKeys
}
